import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Testids } from '@hweeks/kei-costs-ui-testids';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import '../design';
import { readTheme } from '../design/theme';
const ConfirmContext = /*#__PURE__*/ createContext(null);
/** Opens the shared confirmation dialog and resolves to the user's answer. */ export function useConfirm() {
    const ctx = useContext(ConfirmContext);
    if (!ctx) {
        throw new Error('useConfirm must be used inside <ConfirmProvider>');
    }
    return ctx;
}
export function ConfirmProvider({ children }) {
    const [options, setOptions] = useState(null);
    const resolverRef = useRef(null);
    const confirmButtonRef = useRef(null);
    const confirm = useCallback((opts)=>{
        setOptions(opts);
        return new Promise((resolve)=>{
            resolverRef.current = resolve;
        });
    }, []);
    const close = useCallback((ok)=>{
        resolverRef.current?.(ok);
        resolverRef.current = null;
        setOptions(null);
    }, []);
    // Enter confirms, Escape cancels — the keyboard behavior the native dialog
    // had, which we would otherwise lose.
    useEffect(()=>{
        if (!options) return;
        confirmButtonRef.current?.focus();
        const onKey = (e)=>{
            if (e.key === 'Escape') close(false);
            if (e.key === 'Enter') close(true);
        };
        window.addEventListener('keydown', onKey);
        return ()=>window.removeEventListener('keydown', onKey);
    }, [
        options,
        close
    ]);
    return /*#__PURE__*/ _jsxs(ConfirmContext.Provider, {
        value: confirm,
        children: [
            children,
            options && // Mounted above the router, so it carries its own .rhd scope (and the saved theme).
            /*#__PURE__*/ _jsx("div", {
                className: "rhd rhd-portal",
                "data-theme": readTheme() ?? undefined,
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "rhd-overlay rhd-overlay--center",
                    role: "presentation",
                    style: {
                        zIndex: 100
                    },
                    children: [
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            "aria-label": "Cancel",
                            onClick: ()=>close(false),
                            style: {
                                position: 'absolute',
                                inset: 0,
                                border: 0,
                                background: 'transparent',
                                cursor: 'default'
                            }
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            "data-testid": Testids.app.confirmDialog.root,
                            role: "alertdialog",
                            "aria-modal": "true",
                            "aria-label": options.title ?? 'Confirm',
                            className: `rhd-confirm${options.tone === 'danger' ? ' rhd-confirm--danger' : ''}`,
                            style: {
                                position: 'relative'
                            },
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "rhd-confirm__head",
                                    children: [
                                        options.tone === 'danger' && /*#__PURE__*/ _jsx("span", {
                                            className: "rhd-confirm__seal",
                                            children: "削"
                                        }),
                                        /*#__PURE__*/ _jsx("h2", {
                                            className: "rhd-confirm__title",
                                            children: options.title ?? 'Are you sure?'
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "rhd-confirm__msg",
                                    children: options.message
                                }),
                                options.detail && /*#__PURE__*/ _jsx("p", {
                                    className: "rhd-confirm__detail",
                                    children: options.detail
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "rhd-confirm__actions",
                                    children: [
                                        /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            "data-testid": Testids.app.confirmDialog.cancelButton,
                                            onClick: ()=>close(false),
                                            className: "rhd-b rhd-b--paper rhd-b--sm",
                                            style: {
                                                padding: '10px 14px',
                                                fontSize: 13.5
                                            },
                                            children: options.cancelLabel ?? 'Cancel'
                                        }),
                                        /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            ref: confirmButtonRef,
                                            "data-testid": Testids.app.confirmDialog.confirmButton,
                                            onClick: ()=>close(true),
                                            className: `rhd-b ${options.tone === 'danger' ? 'rhd-b--danger' : 'rhd-b--primary'}`,
                                            style: {
                                                padding: '10px 14px',
                                                fontSize: 13.5
                                            },
                                            children: options.confirmLabel ?? 'Confirm'
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
}
